import apiClient from '../client';
import type {
  AlertResponse,
  AlertSummary,
  AlertStatus,
  AcknowledgeAlertRequest,
  ResolveAlertRequest,
  DismissAlertRequest,
} from '@/types/alert.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const alertsApi = {
  /**
   * Lista alertas com filtro por status (paginado)
   * @param status Status do alerta (default: PENDING)
   * @param params Parâmetros de paginação
   */
  getAll: async (
    status: AlertStatus = 'PENDING' as AlertStatus,
    params: PaginationParams = {}
  ): Promise<PaginatedResponse<AlertResponse>> => {
    const response = await apiClient.get<PaginatedResponse<AlertResponse>>('/v1/alerts', {
      params: { status, ...params },
    });
    return response.data;
  },

  /**
   * Lista todos os alertas pendentes priorizados por severidade
   */
  getPending: async (): Promise<AlertResponse[]> => {
    const response = await apiClient.get<AlertResponse[]>('/v1/alerts/pending');
    return response.data;
  },

  /**
   * Lista alertas urgentes pendentes (ideação suicida, etc.)
   */
  getUrgent: async (): Promise<AlertResponse[]> => {
    const response = await apiClient.get<AlertResponse[]>('/v1/alerts/urgent');
    return response.data;
  },

  /**
   * Retorna resumo dos alertas (contagem pendentes e urgentes)
   */
  getSummary: async (): Promise<AlertSummary> => {
    const response = await apiClient.get<AlertSummary>('/v1/alerts/summary');
    return response.data;
  },

  /**
   * Lista alertas de um paciente específico (paginado)
   * @param patientId ID do paciente
   * @param params Parâmetros de paginação
   */
  getByPatient: async (
    patientId: string,
    params: PaginationParams = {}
  ): Promise<PaginatedResponse<AlertResponse>> => {
    const response = await apiClient.get<PaginatedResponse<AlertResponse>>(
      `/v1/alerts/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  /**
   * Busca um alerta por ID
   * @param id ID do alerta
   */
  getById: async (id: string): Promise<AlertResponse> => {
    const response = await apiClient.get<AlertResponse>(`/v1/alerts/${id}`);
    return response.data;
  },

  /**
   * Reconhece um alerta (marca como visto)
   * @param id ID do alerta
   * @param request Notas opcionais sobre o reconhecimento
   */
  acknowledge: async (id: string, request?: AcknowledgeAlertRequest): Promise<AlertResponse> => {
    const response = await apiClient.post<AlertResponse>(`/v1/alerts/${id}/acknowledge`, request || {});
    return response.data;
  },

  /**
   * Resolve um alerta
   * @param id ID do alerta
   * @param request Notas de resolução (obrigatório)
   */
  resolve: async (id: string, request: ResolveAlertRequest): Promise<AlertResponse> => {
    const response = await apiClient.post<AlertResponse>(`/v1/alerts/${id}/resolve`, request);
    return response.data;
  },

  /**
   * Descarta um alerta como falso positivo
   * @param id ID do alerta
   * @param request Motivo do descarte (obrigatório)
   */
  dismiss: async (id: string, request: DismissAlertRequest): Promise<AlertResponse> => {
    const response = await apiClient.post<AlertResponse>(`/v1/alerts/${id}/dismiss`, request);
    return response.data;
  },
};
